import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { Send } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import ThinkingProcessLog from './ThinkingProcessAccordion';
import './ChatInterface.css';

const ChatInterface = forwardRef(({ 
  messages, 
  onSendMessage, 
  loading, 
  compact = false,
  qbConnected = true,
  thinkingProcesses = [],
  currentThinkingProcess = null,
  onProcessComplete = null
}, ref) => {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() && !loading) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue]);

  useEffect(() => {
    if (ref) {
      ref.current = textareaRef.current;
    }
  }, [ref]);

  return (
    <div className={`chat-interface ${compact ? 'compact' : ''}`}>
      {messages.length > 0 && (
        <div className="messages-container">
          {messages.map((message, index) => {
            console.log(`Message ${index}:`, message);
            console.log(`Message ${index} thinkingProcess:`, message.thinkingProcess);
            console.log(`Message ${index} thinkingProcessStatus:`, message.thinkingProcessStatus);
            return (
              <div key={`${message.role}-${index}-${message.thinkingProcessStatus}`} className={`message ${message.role}`}>
                {/* Render log for assistant messages with a thinkingProcess ABOVE the response */}
                {message.role === 'assistant' && message.thinkingProcess && (
                  <ThinkingProcessLog
                    key={`thinking-${index}-${message.thinkingProcess.length}`}
                    isVisible={true}
                    steps={message.thinkingProcess}
                    status={message.thinkingProcessStatus || 'complete'}
                  />
                )}
              <div className="message-content">
                <ReactMarkdown>{message.content}</ReactMarkdown>
              </div>
              <div className="message-time">
                {message.timestamp?.toLocaleTimeString()}
              </div>
            </div>
          );
          })}
          <div ref={messagesEndRef} />
        </div>
      )}
      
      {messages.length === 0 && !compact && (
        <div className="empty-state">
          <p>Start by asking about your finances!</p>
          {!qbConnected && (
            <div className="qb-notice">
              <p>💡 <strong>QuickBooks not connected:</strong> You can still view your conversation history and ask general questions. Connect QuickBooks to analyze your financial data.</p>
            </div>
          )}
          <div className="example-queries">
            <button 
              className="example-query"
              onClick={() => setInputValue("Show me my profit and loss for this quarter")}
            >
              "Show me my profit and loss for this quarter"
            </button>
            <button 
              className="example-query"
              onClick={() => setInputValue("What are my biggest expenses this month?")}
            >
              "What are my biggest expenses this month?"
            </button>
            <button 
              className="example-query"
              onClick={() => setInputValue("How is my cash flow looking?")}
            >
              "How is my cash flow looking?"
            </button>
            <button 
              className="example-query"
              onClick={() => setInputValue("Show me my recent transactions")}
            >
              "Show me my recent transactions"
            </button>
          </div>
        </div>
      )}
      
      <form className="chat-input-form" onSubmit={handleSubmit}>
        <div className="input-container">
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about your finances..."
            className="chat-input"
            disabled={loading}
            rows={1}
          />
          <button 
            type="submit" 
            className="send-button"
            disabled={!inputValue.trim() || loading}
          >
            <Send size={16} />
          </button>
        </div>
      </form>
    </div>
  );
});

export default ChatInterface;